// ============================================================
//  BrandingContext.tsx — Central brand identity provider
//  - Fetches /api/branding on app load
//  - Updates document.title dynamically
//  - Exposes useBranding() hook for all pages
//  - Falls back to AVIRAAPRO ERP defaults if backend unreachable
// ============================================================
import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import apiClient from "./apiClient";
import { toBackendUrl } from "../config";
import { getSelectedCompanyId } from "./companyContext";

export interface Branding {
  app_name: string;
  app_short_name: string;
  tagline: string;
  company_name?: string;
  logo_letter: string;
  logo_url?: string | null;
  favicon_url?: string | null;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  dashboard_title: string;
  footer_text: string;
  version: string;
  support_email?: string;
  support_phone?: string;
}

const DEFAULT_BRANDING: Branding = {
  app_name:         "AVIRAAPRO ERP",
  app_short_name:   "AVIRAAPRO",
  tagline:          "Enterprise Resource Planning",
  company_name:     "",
  logo_letter:      "A",
  logo_url:         null,
  favicon_url:      null,
  primary_color:    "#6366F1",
  secondary_color:  "#8B5CF6",
  accent_color:     "#EC4899",
  dashboard_title:  "AVIRAAPRO ERP — Dashboard",
  footer_text:      "AVIRAAPRO ERP v2.0",
  version:          "2.0.0",
};

interface BrandingContextValue {
  branding: Branding;
  loading: boolean;
  refresh: () => Promise<void>;
  setBranding: (b: Branding) => void;
  getLogoUrl: () => string | null;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: DEFAULT_BRANDING,
  loading: true,
  refresh: async () => {},
  setBranding: () => {},
  getLogoUrl: () => null,
});

export const useBranding = () => useContext(BrandingContext);

// Cache in localStorage so first paint uses last-known values (avoids flash)
const CACHE_KEY = "erp_branding_cache";
const cacheKey = (companyId = getSelectedCompanyId()) => `${CACHE_KEY}_${companyId || "public"}`;

const loadCache = (companyId = getSelectedCompanyId()): Branding => {
  try {
    const raw = localStorage.getItem(cacheKey(companyId));
    if (raw) return { ...DEFAULT_BRANDING, ...JSON.parse(raw) };
  } catch (_) {}
  return DEFAULT_BRANDING;
};

const saveCache = (b: Branding, companyId = getSelectedCompanyId()) => {
  try { localStorage.setItem(cacheKey(companyId), JSON.stringify(b)); } catch (_) {}
};

export const BrandingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [branding, setBrandingState] = useState<Branding>(loadCache);
  const [loading, setLoading]        = useState(true);

  const applyToDom = (b: Branding) => {
    // Update tab title
    document.title = b.app_name;
    // Update CSS custom properties so any styled-component can pick them up
    const root = document.documentElement;
    root.style.setProperty("--brand-primary",   b.primary_color);
    root.style.setProperty("--brand-secondary", b.secondary_color);
    root.style.setProperty("--brand-accent",    b.accent_color);
  };

  const fetchBranding = async () => {
    const requestedCompanyId = getSelectedCompanyId();
    try {
      const res = await apiClient.get("/api/branding/");
      if (res.data?.success && res.data?.data) {
        if (getSelectedCompanyId() !== requestedCompanyId) return;
        const fresh: Branding = { ...DEFAULT_BRANDING, ...res.data.data };
        setBrandingState(fresh);
        saveCache(fresh, requestedCompanyId);
        applyToDom(fresh);
      }
    } catch (e) {
      // Keep cached / default values on failure
      applyToDom(loadCache(requestedCompanyId));
    } finally {
      setLoading(false);
    }
  };

  const setBranding = (b: Branding) => {
    setBrandingState(b);
    saveCache(b);
    applyToDom(b);
  };

  const getLogoUrl = (): string | null => {
    if (!branding.logo_url) return null;
    if (branding.logo_url.startsWith("http")) return branding.logo_url;
    return toBackendUrl(branding.logo_url);
  };

  useEffect(() => {
    const refreshForCompany = () => {
      const cached = loadCache();
      setBrandingState(cached);
      applyToDom(cached);
      fetchBranding();
    };
    window.addEventListener("aviraapro:company-context-changed", refreshForCompany);
    applyToDom(branding);     // immediate — use cache
    fetchBranding();          // then refresh from server
    return () => window.removeEventListener("aviraapro:company-context-changed", refreshForCompany);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, loading, refresh: fetchBranding, setBranding, getLogoUrl }}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
